import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useProductCategories } from '../../../hooks/useProductCategories';
import CategorySelectField from './CategorySelectField';
import InvoiceDescriptionLabelHelp from './InvoiceDescriptionHelp';
import { HOURLY_RECURRING, formatRecurring } from '../../../utils/money';
import { SPECIAL_CATEGORY_NAME } from '../../../types/models/SpecialDeal';
import type { AdminProduct } from '../../../types/models/AdminProduct';
import type {
  ApiResponseAdminProductSingle,
  ApiResponseAdminProductUpdate,
} from '../../../types/api/ApiResponseAdminProduct';

interface EditProductModalProps {
  product: AdminProduct;
  isOpen: boolean;
  onClose: () => void;
  /** Replaces this product's row in the grid from a mutation response (the full, re-grouped row). */
  onUpdated: (updated: AdminProduct) => void;
}

// The per-period billing cadences an existing product can switch between (the `_Product::$recurring`
// enum minus "hourly"). Mirrored client-side so the select only offers valid values; the backend
// re-validates and is the final authority. "hourly" is deliberately absent: an hourly price is per hour
// and the others per period, so the backend refuses any change to or from it — an hourly product
// shows its cadence locked instead.
const RECURRING_OPTIONS = ['once', 'monthly', 'yearly'];

// Backend column limits (`_Product` setters). Enforced client-side to give inline feedback and disable
// Save early; the backend re-validates and its field error (data.{field}) still wins if we're wrong.
const LABEL_MAX = 128;
const DESC_MAX = 255;

interface FieldErrors {
  label?: string;
  product_desc?: string;
  public_desc?: string;
  recurring?: string;
  active?: string;
  tenant_visible?: string;
  category?: string;
  general?: string;
}

/**
 * Full-modification modal for a catalog product (the kebab "Modify" action). Edits every mutable
 * column — label, the invoice-line template (`product_desc`), the customer-facing copy (`public_desc`),
 * billing cadence, active flag, and category — while leaving the immutable `id` (PK) and `product_key`
 * (the invoicing join key) untouched. The scalar default price is a separate table (its own inline
 * editor + overrides modal) and is intentionally not edited here.
 *
 * Two backend endpoints back this one form: the descriptive columns go through PATCH
 * `/admin/products/{id}` (a partial change-set), and category — which that endpoint deliberately
 * ignores — through PATCH `/admin/products/{id}/category`. On save we issue only the calls that
 * actually changed, descriptive first; both return the full updated row so we feed the latest one back
 * to the grid. Mounted only while open (keyed by product id upstream).
 */
function EditProductModal({ product, isOpen, onClose, onUpdated }: EditProductModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useProductCategories(isOpen);

  // `baseline` is what's currently persisted; it advances after each successful call so a retry after
  // a partial failure (e.g. descriptive saved, category rejected) resends only what's still unsaved.
  const [baseline, setBaseline] = useState<AdminProduct>(product);

  const [label, setLabel] = useState(product.label);
  const [productDesc, setProductDesc] = useState(product.product_desc);
  const [publicDesc, setPublicDesc] = useState(product.public_desc ?? '');
  const [recurring, setRecurring] = useState(product.recurring);
  // Status: normally read-only here (it only changes through invoicing-engine wiring) — EXCEPT for
  // the Special category, whose products are billed through deals (generic machinery, no
  // per-product wiring), so staff must be able to activate/retire them from this form. The state
  // only takes effect while the selected category is Special.
  const [active, setActive] = useState(product.active);
  const [tenantVisible, setTenantVisible] = useState(product.tenant_visible);
  const [categoryId, setCategoryId] = useState(product.product_category_id);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Normalise inputs the same way the backend does before comparing / sending.
  const trimmedLabel = label.trim();
  const trimmedDesc = productDesc.trim();
  const normPublic = publicDesc.trim() === '' ? null : publicDesc.trim();

  // Client-side validation (matches the _Product setters).
  const labelError =
    trimmedLabel === '' ? 'Label is required.' : trimmedLabel.length > LABEL_MAX ? `Label cannot exceed ${LABEL_MAX} characters.` : undefined;
  const descError =
    trimmedDesc === ''
      ? 'Invoice description is required.'
      : trimmedDesc.length > DESC_MAX
        ? `Invoice description cannot exceed ${DESC_MAX} characters.`
        : undefined;
  const publicError =
    (normPublic?.length ?? 0) > DESC_MAX ? `Public description cannot exceed ${DESC_MAX} characters.` : undefined;
  const isFormValid = !labelError && !descError && !publicError;

  // The Status switch is editable only when the product IS a Special product server-side
  // (persisted category) AND stays one (selected category). Gating on the PERSISTED category is
  // load-bearing: `active` rides the descriptive PATCH, which runs BEFORE the category PATCH — a
  // selected-only gate could activate a still-non-Special product and then strand it active when
  // the category leg fails. Moving a product into Special therefore takes two saves: first the
  // category, then the status. (Selected-name fallback covers the categories list still loading,
  // since the select seeds from the baseline.)
  const persistedIsSpecial = baseline.category.name === SPECIAL_CATEGORY_NAME;
  const selectedCategoryName =
    categories.find((c) => c.id === categoryId)?.name ??
    (categoryId === baseline.product_category_id ? baseline.category.name : undefined);
  const selectedIsSpecial = selectedCategoryName === SPECIAL_CATEGORY_NAME;
  const statusEditable = persistedIsSpecial && selectedIsSpecial;
  // Hourly is fixed for the product's lifetime (see RECURRING_OPTIONS) and can't live in Special.
  const isHourly = baseline.recurring === HOURLY_RECURRING;

  // The partial change-set of descriptive columns that actually differ from what's persisted.
  const descriptiveChanges: Record<string, string | boolean | null> = {};
  if (trimmedLabel !== baseline.label) descriptiveChanges.label = trimmedLabel;
  if (trimmedDesc !== baseline.product_desc) descriptiveChanges.product_desc = trimmedDesc;
  if (normPublic !== (baseline.public_desc ?? null)) descriptiveChanges.public_desc = normPublic;
  if (recurring !== baseline.recurring) descriptiveChanges.recurring = recurring;
  if (tenantVisible !== baseline.tenant_visible) descriptiveChanges.tenant_visible = tenantVisible;
  // `active` enters the change-set only for persisted-and-staying Special products — elsewhere it
  // stays immutable here. Retiring a Special product that active deals reference is refused by the
  // backend's coupling guard; that error lands under data.active and renders on the Status field.
  if (statusEditable && active !== baseline.active) descriptiveChanges.active = active;
  const categoryChanged = categoryId !== baseline.product_category_id;
  const isUnchanged = Object.keys(descriptiveChanges).length === 0 && !categoryChanged;

  const clearError = (field: keyof FieldErrors) =>
    setErrors((prev) => (prev[field] || prev.general ? { ...prev, [field]: undefined, general: undefined } : prev));

  // Pick the most specific message a failed response offers, for the notification drawer.
  const messageFromUpdate = (body: ApiResponseAdminProductUpdate | null): string =>
    body?.data?.label ??
    body?.data?.product_desc ??
    body?.data?.public_desc ??
    body?.data?.recurring ??
    body?.data?.active ??
    body?.data?.tenant_visible ??
    body?.data?.body ??
    body?.data?.id ??
    body?.message ??
    'Could not update the product. Please try again.';

  const notifyError = (description: string) => {
    addNotification({
      title: 'Could not update product',
      srTitle: 'Error alert',
      description,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (isUnchanged || !isFormValid) {
      return;
    }
    setIsSaving(true);
    setErrors({});

    let latest = baseline;

    // 1. Descriptive columns — PATCH /admin/products/{id}.
    if (Object.keys(descriptiveChanges).length > 0) {
      try {
        const response = await apiFetch(`/admin/products/${baseline.id}`, {
          method: 'PATCH',
          body: JSON.stringify(descriptiveChanges),
        });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseAdminProductUpdate | null;
        if (body?.status === 'success' && body.data?.product) {
          latest = body.data.product;
          setBaseline(latest);
        } else {
          setErrors({
            label: body?.data?.label,
            product_desc: body?.data?.product_desc,
            public_desc: body?.data?.public_desc,
            recurring: body?.data?.recurring,
            active: body?.data?.active,
            tenant_visible: body?.data?.tenant_visible,
            general:
              body?.data?.label ||
              body?.data?.product_desc ||
              body?.data?.public_desc ||
              body?.data?.recurring ||
              body?.data?.active ||
              body?.data?.tenant_visible
                ? undefined
                : (body?.data?.body ?? body?.data?.id ?? body?.message ?? 'Could not update the product.'),
          });
          notifyError(messageFromUpdate(body));
          setIsSaving(false);
          return;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
        setErrors({ general: message });
        notifyError(message);
        setIsSaving(false);
        return;
      }
    }

    // 2. Category — its own endpoint (the descriptive PATCH ignores product_category_id).
    if (categoryId !== latest.product_category_id) {
      try {
        const response = await apiFetch(`/admin/products/${baseline.id}/category`, {
          method: 'PATCH',
          body: JSON.stringify({ product_category_id: categoryId }),
        });
        const body = (await response.json().catch(() => null)) as ApiResponseAdminProductSingle | null;
        if (body?.status === 'success' && body.data?.product) {
          latest = body.data.product;
          setBaseline(latest);
        } else {
          const catError = body?.data?.product_category_id ?? body?.message ?? 'Could not change the category.';
          setErrors({ category: catError });
          // The descriptive save (if any) already succeeded — surface it in the grid so it isn't lost.
          onUpdated(latest);
          notifyError(catError);
          setIsSaving(false);
          return;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
        setErrors({ category: message });
        onUpdated(latest);
        notifyError(message);
        setIsSaving(false);
        return;
      }
    }

    onUpdated(latest);
    addNotification({
      title: 'Product updated',
      srTitle: 'Success alert',
      description: `${latest.label} has been updated.`,
      variant: 'success',
    });
    setDrawerOpen(true);
    setIsSaving(false);
    onClose();
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={onClose} aria-label={`Modify ${product.label}`}>
      <ModalHeader title={`Modify product — ${product.label}`} />
      <ModalBody>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          {errors.general && (
            <Alert variant="danger" isInline title="Could not update product">
              {errors.general}
            </Alert>
          )}

          <FormGroup label="Label" isRequired fieldId="edit-product-label">
            <TextInput
              id="edit-product-label"
              value={label}
              maxLength={LABEL_MAX}
              onChange={(_event, value) => {
                setLabel(value);
                clearError('label');
              }}
              validated={errors.label || labelError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Product label"
            />
            {(errors.label || labelError) && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{errors.label ?? labelError}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Product key" fieldId="edit-product-key">
            <TextInput id="edit-product-key" value={product.product_key} isDisabled aria-label="Product key (immutable)" />
            <FormHelperText>
              <HelperText>
                <HelperTextItem>The invoicing key is immutable and cannot be changed.</HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup
            label="Invoice description"
            isRequired
            fieldId="edit-product-desc"
            labelHelp={<InvoiceDescriptionLabelHelp />}
          >
            <TextArea
              id="edit-product-desc"
              value={productDesc}
              maxLength={DESC_MAX}
              rows={2}
              onChange={(_event, value) => {
                setProductDesc(value);
                clearError('product_desc');
              }}
              validated={errors.product_desc || descError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Invoice line-item description template"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.product_desc || descError ? 'error' : 'default'}>
                  {errors.product_desc ??
                    descError ??
                    'Line-item template. Insert {{tokens}} — filled in when the invoice line is generated (see the help icon).'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Public description" fieldId="edit-product-public-desc">
            <TextArea
              id="edit-product-public-desc"
              value={publicDesc}
              maxLength={DESC_MAX}
              rows={2}
              onChange={(_event, value) => {
                setPublicDesc(value);
                clearError('public_desc');
              }}
              validated={errors.public_desc || publicError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Customer-facing product description"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.public_desc || publicError ? 'error' : 'default'}>
                  {errors.public_desc ??
                    publicError ??
                    'Shown to customers on the pricing page (plain text, no tokens). Leave empty to unset.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Billing cadence" fieldId="edit-product-recurring">
            {isHourly ? (
              <TextInput
                id="edit-product-recurring"
                value={formatRecurring(HOURLY_RECURRING)}
                readOnlyVariant="default"
                aria-label="Billing cadence"
              />
            ) : (
              <FormSelect
                id="edit-product-recurring"
                value={recurring}
                onChange={(_event, value) => {
                  setRecurring(value);
                  clearError('recurring');
                }}
                isDisabled={isSaving}
                aria-label="Billing cadence"
              >
                {RECURRING_OPTIONS.map((option) => (
                  <FormSelectOption key={option} value={option} label={formatRecurring(option)} />
                ))}
              </FormSelect>
            )}
            {(errors.recurring || isHourly) && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={errors.recurring ? 'error' : 'default'}>
                    {errors.recurring ??
                      "An hourly product's price is per hour and the other cadences are priced per period, so its cadence can't be changed."}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Category" fieldId="edit-product-category">
            <CategorySelectField
              idPrefix="edit-product"
              categories={categories}
              categoriesLoading={categoriesLoading}
              categoriesError={categoriesError}
              value={categoryId}
              onChange={(id) => {
                setCategoryId(id);
                clearError('category');
              }}
              refetchCategories={refetchCategories}
              isDisabled={isSaving}
              error={errors.category}
              fallbackOption={{ id: baseline.product_category_id, name: baseline.category.name ?? '—' }}
              disabledCategory={
                isHourly ? { name: SPECIAL_CATEGORY_NAME, reason: 'not allowed for hourly products' } : undefined
              }
            />
          </FormGroup>

          <FormGroup label="Status" fieldId="edit-product-active">
            <Switch
              id="edit-product-active"
              label="Active"
              // baseline (not the prop) — it advances after a partial save, so a retry never
              // shows a value the server no longer holds.
              isChecked={statusEditable ? active : baseline.active}
              onChange={(_event, checked) => {
                setActive(checked);
                clearError('active');
              }}
              isDisabled={isSaving || !statusEditable}
              aria-label="Product active status"
            />
            <FormHelperText>
              <HelperText>
                {/* The Special-deal coupling guard refuses retiring a product that active deals
                    reference — that rejection arrives under data.active and renders here. */}
                <HelperTextItem variant={errors.active ? 'error' : 'default'}>
                  {errors.active ??
                    (statusEditable
                      ? 'Special products are billed through deals, so their status is editable: a deal can only reference an active product, and retiring is refused while active deals still reference it (deactivate the deals first).'
                      : selectedIsSpecial && !persistedIsSpecial
                        ? 'Save the category change first — the status becomes editable once the product is persisted in the Special category.'
                        : persistedIsSpecial && !selectedIsSpecial
                          ? 'The status stays locked while the product is being moved out of the Special category.'
                          : "The active status is managed by the invoicing engine and can't be changed here — it only changes as billing support for this product is developed.")}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Customer visibility" fieldId="edit-product-tenant-visible">
            <Switch
              id="edit-product-tenant-visible"
              label="Visible on customer pricing pages"
              isChecked={tenantVisible}
              onChange={(_event, checked) => {
                setTenantVisible(checked);
                clearError('tenant_visible');
              }}
              isDisabled={isSaving}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.tenant_visible ? 'error' : 'default'}>
                  {errors.tenant_visible ??
                    'Off = hidden-but-billable: the product stays off customer pricing pages while billing (and Special deals) keep working. Distinct from Status, which stops billing.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          isLoading={isSaving}
          isDisabled={isSaving || isUnchanged || !isFormValid}
        >
          Save
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default EditProductModal;
